import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

//css
import { Button, Table, Modal, Row, Col, Form, FloatingLabel } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo, faEdit } from "@fortawesome/free-solid-svg-icons";

//pages
import AddHospital from "./AddHospital.js";
import AppPagination from "../components/Pagination";
import AppNav from "../../header/AppNav";
import AlertModal from "../../components/AlertModal";
import { HospitalService } from "../../services/HospitalService";

function Hospitals() {
  const [addModalShow, setAddModalShow] = useState(false);
  const [hospitals, setHospitals] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Modal states
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [modalData, setModalData] = useState({});
  
  // Edit modal states
  const [editModalShow, setEditModalShow] = useState(false);
  const [editHospital, setEditHospital] = useState(null);
  const [editFormData, setEditFormData] = useState({
    id: "",
    hospitalName: "",
    contactPersonName: "",
    contactNumber: "",
    optionalContactNumber: "",
    emailId: ""
  });

  const getHospitals = async () => {
    setLoading(true);
    try {
      const res = await HospitalService.getAllHospitals();
      if (res.status === 200) {
        const hospitalArray = Object.keys(res)
          .filter(key => key !== 'status')
          .map(key => res[key]);
        setHospitals(hospitalArray);
      } else {
        setHospitals([]);
        setModalData({
          title: "Error",
          message: "Failed to fetch hospitals",
          variant: "error"
        });
        setShowAlertModal(true);
      }
    } catch (error) {
      console.error("Error fetching hospitals:", error);
      setModalData({
        title: "Error",
        message: error.message || "Failed to fetch hospitals",
        variant: "error"
      });
      setShowAlertModal(true);
      setHospitals([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getHospitals();
  }, []);

  // Edit hospital functions
  const handleEditClick = (hospital) => {
    setEditHospital(hospital);
    setEditFormData({
      id: hospital.id || "",
      hospitalName: hospital.hospitalName || "",
      contactPersonName: hospital.contactPersonName || "",
      contactNumber: hospital.contactNumber || "",
      optionalContactNumber: hospital.optionalContactNumber || "",
      emailId: hospital.emailId || ""
    });
    setEditModalShow(true);
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleUpdateHospital = async () => {
    // Basic validation
    if (!editFormData.hospitalName || !editFormData.contactPersonName || !editFormData.contactNumber || !editFormData.emailId) {
      setModalData({
        title: "Validation Error",
        message: "Please fill in all required fields",
        variant: "error"
      });
      setShowAlertModal(true);
      return;
    }

    setLoading(true);
    try {
      const res = await HospitalService.updateHospital(editFormData);
      if (res.status === 200) {
        setEditModalShow(false); // Close edit modal first
        getHospitals(); // Refresh the list
        
        setModalData({
          title: "Success",
          message: res.message || "Hospital updated successfully!",
          variant: "success"
        });
        setShowAlertModal(true);
      } else {
        setModalData({
          title: "Error",
          message: "Error while updating hospital",
          variant: "error"
        });
        setShowAlertModal(true);
      }
    } catch (error) {
      setModalData({
        title: "Error",
        message: error.message || "Failed to update hospital",
        variant: "error"
      });
      setShowAlertModal(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <AppNav />
      <h1 className="list-style">Hospitals</h1>
      <div className="list-table-style">
        <div className="add-button">
          <Button onClick={() => setAddModalShow(true)}>Add Hospital</Button>
          <AddHospital
            show={addModalShow}
            onHide={() => setAddModalShow(false)}
            onClose={() => {
              setAddModalShow(false);
              getHospitals();
            }}
          />
        </div>
        <Table striped bordered hover>
          <thead>
            <tr>
              <th>Sl no</th>
              <th className="list-item-align-center">Hospital Name</th>
              <th className="list-item-align-center">Contact Person</th>
              <th className="list-item-align-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="5" className="text-center">Loading...</td>
              </tr>
            ) : hospitals.length > 0 ? (
              hospitals.map((hospital, index) => (
                <tr key={hospital.id}>
                  <td>{index + 1}</td>
                  <td className="list-item-align-center">{hospital.hospitalName}</td>
                  <td className="list-item-align-center">{hospital.contactPersonName}</td>
                  <td className="list-item-align-center">
                    <FontAwesomeIcon
                      icon={faEdit}
                      style={{ color: '#05275a' }}
                      size="lg"
                      className="custom-icon"
                      onClick={() => handleEditClick(hospital)}
                      title="Edit Hospital"
                    />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="text-center">No hospitals found</td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Custom Modals */}
      <AlertModal
        show={showAlertModal}
        onHide={() => setShowAlertModal(false)}
        title={modalData.title}
        message={modalData.message}
        variant={modalData.variant}
      />

      {/* Edit Hospital Modal */}
      <Modal
        show={editModalShow}
        onHide={() => setEditModalShow(false)}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter">
            Edit Hospital
          </Modal.Title>
        </Modal.Header>
        <div className="input-modal-spacing">
          <Row>
            <Col>
              <FloatingLabel
                controlId="editHospital"
                label="Hospital Name*"
                className="mb-3"
              >
                <Form.Control
                  type="text"
                  placeholder="Hospital Name*"
                  name="hospitalName"
                  value={editFormData.hospitalName}
                  onChange={handleEditInputChange}
                />
              </FloatingLabel>
            </Col>
            <Col>
              <FloatingLabel
                controlId="editContactName"
                label="Contact Person Name*"
                className="mb-3"
              >
                <Form.Control
                  type="text"
                  placeholder="Contact Person Name*"
                  name="contactPersonName"
                  value={editFormData.contactPersonName}
                  onChange={handleEditInputChange}
                />
              </FloatingLabel>
            </Col>
          </Row>

          <Row>
            <Col>
              <FloatingLabel
                controlId="editHospitalContact"
                label="Hospital Contact no*"
                className="mb-3"
              >
                <Form.Control
                  type="text"
                  placeholder="Hospital Contact no*"
                  name="contactNumber"
                  value={editFormData.contactNumber}
                  onChange={handleEditInputChange}
                />
              </FloatingLabel>
            </Col>
            <Col>
              <FloatingLabel
                controlId="editOptionalContact"
                label="Optional Contact no"
                className="mb-3"
              >
                <Form.Control
                  type="text"
                  placeholder="Optional Contact no"
                  name="optionalContactNumber"
                  value={editFormData.optionalContactNumber}
                  onChange={handleEditInputChange}
                />
              </FloatingLabel>
            </Col>
            <Col>
              <FloatingLabel
                controlId="editEmail"
                label="Email*"
                className="mb-3"
              >
                <Form.Control
                  type="email"
                  placeholder="Email*"
                  name="emailId"
                  value={editFormData.emailId}
                  onChange={handleEditInputChange}
                />
              </FloatingLabel>
            </Col>
          </Row>
        </div>
        <hr className="color-grey" />
        <Row className="modal-button-group-spacing">
          <Col className="modal-add-button-spacing">
            <Button onClick={handleUpdateHospital} disabled={loading}>
              {loading ? "Updating..." : "Update"}
            </Button>
          </Col>
          <Col className="modal-cancel-button-spacing">
            <Button onClick={() => setEditModalShow(false)} variant="dark">
              Cancel
            </Button>
          </Col>
          <Col />
          <Col />
          <Col />
          <Col />
          <Col />
          <Col />
          <Col />
        </Row>
      </Modal>
    </div>
  );
}

export default Hospitals;
